import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import React from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { useExchangeRateHandler } from '../../../hooks/useExchangeRateHandler'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'
import DatePickerPopup from '@/ui/date/DatePickerPopup'
import { faCalendar, faCaretDownSolid, FaIcon } from '@/lib/fa'


type DateEditorProps = {
  value: any
  onCommit: (newDate: string | null) => void
  placeholder?: string
}

const toISODate = (date: any): string | null => {
  if (!date) return null
  if (moment.isMoment(date)) return date.format('YYYY-MM-DD')
  if (typeof date === 'string') return moment(date).format('YYYY-MM-DD')
  return null
}

const formatDateWithDashes = (digits: string): string => {
  let result = ''
  for (let i = 0; i < digits.length; i++) {
    if (i === 4 || i === 6) result += '-'
    result += digits[i]
  }
  return result
}

const parseDateInput = (input: string): string | null => {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const day = parseInt(match[3], 10)
  if (year < 1900 || year > 2100) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  )
    return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const DateEditor: React.FC<DateEditorProps> = ({
  value,
  onCommit,
  placeholder = 'RRRR-MM-DD',
}) => {
  const [inputValue, setInputValue] = React.useState('')
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const [popupPosition, setPopupPosition] = React.useState({ top: 0, left: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const cursorPosRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setInputValue(toISODate(value) || '')
  }, [value])

  React.useEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      const pos = cursorPosRef.current
      inputRef.current.setSelectionRange(pos, pos)
      cursorPosRef.current = null
    }
  })

  React.useEffect(() => {
    if (!isPickerOpen) return undefined
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        popupRef.current &&
        !popupRef.current.contains(e.target as Node)
      ) {
        setIsPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isPickerOpen])

  const openPopup = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    })
    setIsPickerOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart ?? newValue.length
    const newDigits = newValue.replace(/\D/g, '').slice(0, 8)
    const formatted = formatDateWithDashes(newDigits)

    const digitsBeforeCursor = newValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length
    let newCursorPos = 0
    let digitCount = 0
    for (let i = 0; i < formatted.length; i++) {
      if (digitCount === digitsBeforeCursor) break
      newCursorPos = i + 1
      if (formatted[i] !== '-') digitCount++
    }
    if (
      newCursorPos < formatted.length &&
      formatted[newCursorPos] === '-' &&
      newValue.length >= inputValue.length
    ) {
      newCursorPos++
    }

    cursorPosRef.current = newCursorPos
    setInputValue(formatted)
  }

  const handleBlur = () => {
    const parsed = parseDateInput(inputValue)
    if (parsed) {
      onCommit(parsed)
    } else if (inputValue === '') {
      onCommit(null)
    } else {
      setInputValue(toISODate(value) || '')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  const handlePopupSubmit = (fullDate: string) => {
    setInputValue(fullDate)
    onCommit(fullDate)
    setIsPickerOpen(false)
  }

  return (
    <>
      <div
        ref={containerRef}
        className="tw-flex tw-w-fit tw-items-center tw-gap-1 tw-rounded tw-py-1"
      >
        <FaIcon
          icon={faCalendar}
          className="tw-cursor-pointer"
          onClick={() => {
            if (isPickerOpen) {
              setIsPickerOpen(false)
            } else {
              openPopup()
            }
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="tw-w-[100px] tw-text-nowrap tw-bg-transparent tw-text-sm tw-text-content-base tw-placeholder-content-base/50 focus:tw-outline-none"
        />
      </div>
      {isPickerOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="tw-absolute tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            style={{ top: popupPosition.top, left: popupPosition.left }}
          >
            <DatePickerPopup
              value={toISODate(value)}
              submit={handlePopupSubmit}
            />
          </div>,
          document.body,
        )}
    </>
  )
}

const AccomodationRowItem = ({
  companyId,
  onUpdateArrayItem,
  isChecked,
  toggleId,
  originalIndex,
  acc,
  currencies,
}: {
  companyId: string
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  isChecked: boolean
  toggleId: (id: number) => void
  originalIndex: number
  accommodations: any
  acc: any
  currencies: any[]
}) => {
  const { t, activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const { data: countries = [] } = useQueryCountries({ companyId })

  const {
    handleCurrencyChange,
    handleExchangeRateDateChange,
    handleExchangeRateManualChange,
    handleAmountChange,
  } = useExchangeRateHandler({
    companyId,
    onUpdateArrayItem: onUpdateArrayItem!,
    arrayName: 'accommodations',
  })

  const formatAmount = (value: number): string => {
    if (value == null || isNaN(Number(value))) return '-'
    return Number(value).toFixed(2)
  }

  const formatRate = (value: number): string => {
    if (value == null || isNaN(Number(value))) return '-'
    return Number(value).toFixed(4)
  }

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] =
    React.useState(false)
  const [countrySearch, setCountrySearch] = React.useState('')
  const countryDropdownRef = React.useRef<HTMLDivElement>(null)
  const countrySearchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isCountryDropdownOpen) {
      setCountrySearch('')
      return undefined
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCountryDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    countrySearchRef.current?.focus()
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCountryDropdownOpen])

  const handleSelectCountry = (country: any) => {
    if (originalIndex !== -1 && onUpdateArrayItem && country) {
      onUpdateArrayItem('accommodations', originalIndex, { country })
    }
    setIsCountryDropdownOpen(false)
  }

  const filteredCountries = React.useMemo(() => {
    if (!countrySearch.trim()) return countries
    const q = countrySearch.trim().toLowerCase()
    return countries.filter((c: any) =>
      translateCountryName(c.name, lang).toLowerCase().includes(q),
    )
  }, [countries, countrySearch, lang])

  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] =
    React.useState(false)
  const currencyDropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isCurrencyDropdownOpen) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCurrencyDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCurrencyDropdownOpen])

  const handleSelectCurrency = (currency: any) => {
    if (originalIndex !== -1 && currency) {
      handleCurrencyChange(acc, originalIndex, currency.code, currency.id)
    }
    setIsCurrencyDropdownOpen(false)
  }

  return (
    <tr className="hover:tw-bg-base-50">
      <TableCheckbox
        disabled={false}
        checked={isChecked}
        onChange={() => toggleId(originalIndex)}
      />

      {/* date */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <DateEditor
          value={acc.date}
          onCommit={(newDate) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('accommodations', originalIndex, {
                date: newDate ? moment(newDate) : undefined,
              })
            }
          }}
        />
      </td>

      {/* city */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={acc.city || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('accommodations', originalIndex, {
                city: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      {/* country */}
      <td
        className="tw-min-w-[200px] tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={countryDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {acc.country
                ? translateCountryName(acc.country.name, lang)
                : t('newDelegation.accomodation.choose-country')}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isCountryDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-flex tw-max-h-[320px] tw-min-w-[220px] tw-flex-col tw-rounded tw-border tw-border-base-300 tw-bg-white tw-shadow-lg">
              <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300 tw-p-2">
                <input
                  ref={countrySearchRef}
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder={t('menu.search')}
                  className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1 tw-text-sm focus:tw-border-primary focus:tw-outline-none"
                />
              </div>
              <div className="tw-flex-1 tw-overflow-y-auto tw-py-1">
                {filteredCountries.map((c: any) => (
                  <div
                    key={c.id}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                    onClick={() => handleSelectCountry(c)}
                  >
                    <span>{translateCountryName(c.name, lang)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </td>

      {/* amount */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          inputMode="decimal"
          defaultValue={formatAmount(acc.amount)}
          key={`amount-${originalIndex}-${acc.amount}`}
          onBlur={(e) => {
            const val = e.target.value.replace(',', '.')
            if (originalIndex !== -1) {
              handleAmountChange(acc, originalIndex, Number(val) || 0)
            }
          }}
          className="tw-w-24 tw-rounded tw-py-1 tw-text-sm focus:tw-outline-none"
        />
      </td>

      {/* currency */}
      <td
        className="tw-min-w-[120px] tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={currencyDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsCurrencyDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {acc.currency?.code || '-'}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isCurrencyDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-max-h-[320px] tw-min-w-[120px] tw-overflow-y-auto tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg">
              {currencies.map((c: any) => (
                <div
                  key={c.code || c.id}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                  onClick={() => handleSelectCurrency(c)}
                >
                  <span>{c.code}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>

      {/* exchangeRate */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          inputMode="decimal"
          defaultValue={formatRate(acc.exchangeRate)}
          key={`rate-${originalIndex}-${acc.exchangeRate}`}
          onBlur={(e) => {
            const val = e.target.value.replace(',', '.')
            if (originalIndex !== -1) {
              handleExchangeRateManualChange(
                acc,
                originalIndex,
                Number(val) || 0,
              )
            }
          }}
          className="tw-w-24 tw-rounded tw-py-1 tw-text-sm focus:tw-outline-none"
        />
      </td>

      {/* exchangeRateDate */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <DateEditor
          value={acc.exchangeRateDate}
          onCommit={(newDate) => {
            if (originalIndex !== -1 && newDate) {
              handleExchangeRateDateChange(acc, originalIndex, newDate)
            }
          }}
        />
      </td>

      {/* table */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={acc.table || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('accommodations', originalIndex, {
                table: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      {/* invoiceNumber */}
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={acc.invoiceNumber || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('accommodations', originalIndex, {
                invoiceNumber: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      {/* plnAmount (read-only) */}
      <TableTextRow>{formatAmount(acc.plnAmount)}</TableTextRow>
    </tr>
  )
}

export default AccomodationRowItem
